import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';

// ---------------------------------------------------------------------------
// CRM Event — Outbox row representing a single side-effect to push to a CRM.
//
// Pattern: producers write a row in the same logical step as the local DB
// update (lead status changed, reply received, sent confirmation, ...).
// The crm-tick-worker drains this outbox asynchronously, with retries and
// rate-limit handling. Keeps the sync endpoint fast and resilient against
// CRM outages.
//
// `payload` is a JSON-serialized object whose shape depends on `eventType`.
// ---------------------------------------------------------------------------

@Entity()
export class CrmEvent extends MongoEntity {
  static override _class = 'CrmEvent';
  static override _collectionName = 'crm_events';

  @Field(type => String) userId!: string;
  @Field(type => String) provider!: string;
  @Field(type => String) connectionId!: string;

  /**
   * 'upsert_contact'        → ensures a CRM contact exists for the lead
   * 'log_email_sent'        → adds an Email engagement / note entry
   * 'log_email_reply'       → adds an Email engagement / note for inbound reply
   * 'log_linkedin_sent'     → adds a Note entry (LinkedIn outbound)
   * 'log_linkedin_reply'    → adds a Note entry (LinkedIn inbound)
   */
  @Field(type => String) eventType!: string;

  @Field(type => String) leadId!: string;
  @Field(type => String) campaignId?: string;

  /** JSON-serialized event-specific payload (see eventType) */
  @Field(type => String) payload!: string;

  /** 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' */
  @Field(type => String) status!: string;
  /** ISO timestamp — earliest time this event may execute */
  @Field(type => String) scheduledAt!: string;
  @Field(type => String) attemptedAt?: string;
  @Field(type => String) completedAt?: string;
  @Field(type => String) errorMessage?: string;

  @Field(type => Int) retries!: number;

  @Field(type => String) createdAt!: string;
}
